import { ITextView } from 'flux-diagram-composer';
import { ConnectorDisplayView } from './connector-display-view';
import { Rectangle } from 'flux-core';
import { TextPostion } from 'flux-diagram-composer';
import { ConnectorTextView } from './text/connector-text-view';
import { ConnectorTextModel } from './../model/text/connector-text.mdl';
import { Shape, Graphics } from '@creately/createjs-module';
import { ConnectorRenderView } from 'flux-diagram-composer';
import { ConnectorModel } from '../model/connector.mdl';
import { IGraphics } from 'flux-definition';

const MASK_PADDING = 250;

export class ConnectorView extends ConnectorRenderView {

    /**
     * The shape to set as the mask for the connector in order to
     * make the background of connector texts transparent
     */
    protected _textMask: Shape;

    /**
     * The vertical padding value
     */
    protected textMaskPaddingVertical = 3;

    /**
     * The horizontal padding value
     */
    protected textMaskPaddingHorizontal = 5;

    /**
     * The minimum clickble thinkness a connector should have
     * a hitArea of this size is added to all connectors.
     */
    protected minLineThickness: number = 20;

    /**
     * This is the easeljs view of the shape that will contain
     * all the peices that show the shape. Every view part of the shape
     * must be added into this container.
     */
    protected container: ConnectorDisplayView;

    /**
     * The easeljs representation of drawing space. Everything
     * will be drawn on this Shape object
     */
    protected shape: Shape;

    /**
     * The shape for connector hitArea.
     * hitArea will be the same as connector but
     * with extra thickness and it's invicleble.
     */
    protected _hitArea: Shape;

    /**
     * The model associated with this connector.
     * @override
     */
    public get model(): ConnectorModel {
        return <ConnectorModel>this._model;
    }

    /**
     * Getter that exposes the shape property.
     * Everything will be drawn on this Shape object.
     */
    public get shapeView(): Shape {
        return this.shape;
    }

    /**
     * Externaly set the shape which draws the connector path on.
     */
    public set shapeView( shape: Shape ) {
        this.shape = shape;
    }

    /**
     * Expected hitArea IGraphics instance attached to this connector
     * @override
     */
    public get hitArea(): Graphics {
        if ( this._hitArea ) {
            return this._hitArea.graphics;
        }
    }

    /**
     * Expected output of a connector.
     * @override
     */
    public get renderedShape(): ConnectorDisplayView {
        return this.container;
    }

    /**
     * Expected IGraphics instance attached to this connector
     * @override
     */
    public get graphics(): Graphics {
        return this.shape.graphics;
    }

    /**
     * Return a padding value which will expand the bounded rectangle.
     */
    public get maskPadding() {
        return MASK_PADDING;
    }

    /**
     * Called when the connector is added.
     * @override
     */
    public initialize() {
        this.container = new ConnectorDisplayView( this.id, this.model.type );

        this.shape = new Shape();
        this.container.convertToCursorDrawable( this.shape );
        this.shape.name = this.id;

        this._textMask = new Shape();
        this.shape.mask = this._textMask;

        this.container.addChild( this.shape );
    }

    /**
     * Supporting method which is fired before draw. Setting styles
     * and draw preparation can be done here. This may be replaced by
     * the Entry Class of the View Definition
     * @override
     */
    public beginDraw() {
        this.beginDrawHitArea();
        this.updateTextsMask();
        super.beginDraw();
    }

    /**
     * This function sets a hitArea for the connector no matter the size
     * of the conector.
     */
    public beginDrawHitArea() {
        if ( !this._hitArea ) {
            /**
             * FIXME:
             * Will be changed after draw(graphics, model) API is implemented
             */
            this._hitArea = new Shape();
            this.shape.hitArea = this._hitArea;
        }
        this.hitArea.clear();
        this.hitArea.setStrokeStyle( this.minLineThickness, 'round' ).beginStroke( '#F00' );
    }

    /**
     * Supporting method which is fired after draw. Closing styles
     * and draw ending can be done here. This may be replaced by
     * the Entry Class of the View Definition
     * @override
     */
    public endDraw() {
        super.endDraw();
        if ( this.hitArea ) {
            this.hitArea.endStroke();
            const bounds = this.model.bounds.pad( this.minLineThickness / 2 );
            this._hitArea.cache( bounds.x, bounds.y, bounds.width, bounds.height );
        }
    }

    /**
     * This function creates a text view from the given
     * text model and adds it to the container and text views array
     * @param ShapeTextModel
     * @override
     */
    protected addText( text: ConnectorTextModel ) {
        const textView = new ConnectorTextView( text );
        this.container.addChild( textView );
        this._texts.push( textView );
    }

    /**
     * This removes the text view from the text view list. This also
     * must ensure it is removed from the rendering.
     * @param text The text view to remove.
     */
    protected removeText( text: ITextView ) {
        super.removeText( text );
        this.renderedShape.removeChild( text as any );
    }

    /**
     * This function draws the text mask considering all the available texts
     * on the connector and makes the back ground of the each connector text
     * transparent
     */
    protected updateTextsMask() {
        if ( !this._texts ) {
            return;
        }
        this._textMask.graphics.clear();
        this._textMask.graphics.beginFill( '#F00' );
        this._texts.forEach(( textView: ConnectorTextView ) => {
            const textmodel = this.model.texts[ textView.name ];
            const b = new Rectangle( 0, 0, textmodel.width, textmodel.height );
            if ( b.width === 0 || b.height === 0 || !textmodel.plainText ) { // if textView area is 0
                return;
            }
            const rect = TextPostion
                .getRectangleOnConnector( this.model, textmodel, new Rectangle( b.x, b.y, b.width, b.height ))
                .pad([
                    this.textMaskPaddingVertical,
                    this.textMaskPaddingHorizontal,
                    this.textMaskPaddingVertical,
                    this.textMaskPaddingHorizontal,
                ]);
            this.drawRect( this._textMask.graphics, rect.x, rect.y, rect.width, rect.height );
        });

        const bounds = this.model.bounds.pad( this.maskPadding );
        this.drawRect( this._textMask.graphics, bounds.x + bounds.width , bounds.y, - bounds.width , bounds.height );
        this._textMask.graphics.endFill();
    }

    /**
     * This function draws a rectangle made from lines for the given graphics
     * FIXME
     * The ideal method is to use IGraphics drawRect method but for Safari browser
     * drawRect method with minus width / height doesn't work as expected
     */
    protected drawRect( graphics: IGraphics, x: number, y: number, width: number, height: number ) {
        graphics.moveTo( x, y );
        graphics.lineTo( x + width, y );
        graphics.lineTo( x + width, y + height );
        graphics.lineTo( x, y + height );
        graphics.lineTo( x, y );
    }


}
